import LockIcon from '@mui/icons-material/Lock';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import FileUploader from 'components/ui/file-uploader/FileUploader';
import dayjs from 'dayjs';
import { IMaintenance } from 'features/inventory/maintenance/types';
import { DateSelectorDialog } from 'features/inventory/maintenance/components/dialog/DateSelectorDialog';
import { useTranslation } from 'react-i18next';
import { maintenanceService } from 'shared/services';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { baseCell, compliantCell } from 'components/cells';
import { filterOperators, dateFilterOperators, booleanFilterOperators } from 'components/enhanced';

export type MaintenanceColumnsMode = 'default' | 'detail' | 'expiration' | 'archive';

export const MaintenanceColumns = (
  openDialog?: (id: number, expectedDate?: string) => void,
  mode: MaintenanceColumnsMode = 'default'
): GridColDef[] => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();

  const showEquipmentColumns = mode === 'detail' || mode === 'expiration';

  const isArchive = mode === 'archive';
  const isDirectorUser = useIsDirector();

  const columns = [];

  if (showEquipmentColumns) {
    columns.push({
      field: 'equipment',
      headerName: t(`global.equipment`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    });
    columns.push({
      field: 'description',
      headerName: t(`global.description`),
      width: 300,
      filterOperators,
      renderCell: baseCell
    });
    columns.push({
      field: 'serial_number',
      headerName: t(`entities.equipment.serial_number`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    });
    columns.push({
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    });
    columns.push({
      field: 'position',
      headerName: t(`entities.equipment.position`),
      width: 200,
      filterOperators,
      valueGetter: (params) => params.row?.position ?? '',
      renderCell: baseCell
    });
    columns.push({
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    });
  }

  [
    {
      field: 'expected_month',
      headerName: t(`entities.maintenance.expected_month`),
      width: 100,
      filterOperators: dateFilterOperators,
      renderCell: (params) => {
        const dateStr = params.value;
        if (!dateStr) return '';
        const parts = dateStr.split('/');
        if (parts.length !== 3) return dateStr;
        const month = parts[1];
        const year = parts[2];
        return `${month}/${year}`;
      }
    },
    {
      field: 'expected_date',
      headerName: t(`entities.maintenance.expected_date`),
      width: 100,
      filterOperators: dateFilterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <div>
            <DateSelectorDialog
              dateField="expected_date"
              entityId={maintenance.id}
              initialDate={
                maintenance.expected_date ? dayjs(maintenance.expected_date, 'DD/MM/YYYY') : null
              }
              initialMonth={maintenance.expected_month}
            />
          </div>
        );
      }
    },
    {
      field: 'execution_date',
      headerName: t(`entities.maintenance.execution_date`),
      width: 100,
      filterOperators: dateFilterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <div>
            <span>{maintenance.execution_date}</span>
          </div>
        );
      }
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 100,
      filterOperators,
      valueGetter: (params) => {
        const displayName = params?.row?.status?.display_name;
        if (!displayName) return '';
        const maintenanceKey = `pages.maintenance.status_options.${displayName}`;
        const translated = t(maintenanceKey);
        if (translated !== maintenanceKey) return translated;
        return t(`pages.tickets.status_options.${displayName}`, {
          defaultValue: displayName
        });
      },
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      minWidth: 150,
      flex: 1,
      filterOperators,
      sortable: false,
      renderCell: baseCell
    },
    {
      field: 'compliant',
      headerName: t(`global.compliant`),
      width: 150,
      filterOperators: booleanFilterOperators,
      renderCell: compliantCell()
    },
    {
      field: 'technical_report',
      headerName: t(`${translationPrefix}.technical_report`),
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <IsTenantAdministrator>
            <div>
              <FileUploader
                service={maintenanceService}
                entityId={maintenance.id}
                attachmentName={'technical_report'}
                filename={maintenance.technical_report}
              />
            </div>
          </IsTenantAdministrator>
        );
      },
      width: 80,
      filterable: false,
      sortable: false
    },
    {
      field: 'checklist',
      headerName: t(`${translationPrefix}.checklist`),
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <IsTenantAdministrator>
            <div>
              <FileUploader
                service={maintenanceService}
                entityId={maintenance.id}
                attachmentName={'checklist'}
                filename={maintenance.checklist}
              />
            </div>
          </IsTenantAdministrator>
        );
      },
      width: 80,
      filterable: false,
      sortable: false
    }
  ].forEach((column) => columns.push(column));

  if (!isArchive && !isDirectorUser) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const maintenance: IMaintenance = params.row;
        return [
          {
            label: 'Chiudi manutenzione',
            icon: <LockIcon fontSize="small" sx={{ color: '#334155' }} />,
            onClick: () => openDialog(+maintenance.id, maintenance.expected_date)
          }
        ];
      },
      filterable: false
    });
  }

  return columns;
};
